// skipcq: JS-W1028, JS-0128
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { CRAWL_SCHEDULES_COLLECTION } from '../../utils/firestoreCollections';
import {
  buildScheduleId,
  CADENCES,
  CRAWL_TYPES,
  DATETIME_INPUT_LABEL_PROPS,
  SCHEDULE_FIELD_DEFAULTS,
  STATUSES,
  TARGET_TYPES,
} from './scheduleFormConstants';

const defaultFormState = () => ({
  ...SCHEDULE_FIELD_DEFAULTS,
  targetId: '',
});

function formatDateForDatetimeLocal(value) {
  if (value == null) {
    return '';
  }
  const dateObj = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dateObj.getTime())) {
    return '';
  }
  const pad = (num) => String(num).padStart(2, '0');
  return `${dateObj.getFullYear()}-${pad(dateObj.getMonth() + 1)}-${pad(dateObj.getDate())}T${pad(dateObj.getHours())}:${pad(dateObj.getMinutes())}`;
}

function CreateScheduleDialogIntro({ isEdit, scheduleId }) {
  if (isEdit) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 0.5 }}>
        Schedule ID: <code>{scheduleId}</code>
        <br />
        Crawl type, target type, and target ID cannot be changed; delete and recreate to retarget.
      </Typography>
    );
  }
  return (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 0.5 }}>
      Document id is deterministic:{' '}
      <code>{'{crawlType}__{targetType}__{targetId}'}</code>
    </Typography>
  );
}

function CreateScheduleFormFields({
  crawlType,
  targetType,
  targetId,
  cadence,
  status,
  nextDueLocal,
  onCrawlTypeChange,
  onTargetTypeChange,
  onTargetIdChange,
  onCadenceChange,
  onStatusChange,
  onNextDueChange,
  identityFieldsDisabled = false,
}) {
  return (
    <Stack spacing={2}>
      <FormControl fullWidth required disabled={identityFieldsDisabled}>
        <InputLabel id="create-schedule-crawl-type-label">Crawl type</InputLabel>
        <Select
          labelId="create-schedule-crawl-type-label"
          label="Crawl type"
          value={crawlType}
          onChange={onCrawlTypeChange}
        >
          {CRAWL_TYPES.map((crawlTypeOption) => (
            <MenuItem key={crawlTypeOption} value={crawlTypeOption}>
              {crawlTypeOption}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth required disabled={identityFieldsDisabled}>
        <InputLabel id="create-schedule-target-type-label">Target type</InputLabel>
        <Select
          labelId="create-schedule-target-type-label"
          label="Target type"
          value={targetType}
          onChange={onTargetTypeChange}
        >
          {TARGET_TYPES.map((targetTypeOption) => (
            <MenuItem key={targetTypeOption} value={targetTypeOption}>
              {targetTypeOption}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label="Target ID (brewery document id)"
        value={targetId}
        onChange={onTargetIdChange}
        required
        fullWidth
        disabled={identityFieldsDisabled}
        helperText="Must exist in breweries collection"
      />
      <FormControl fullWidth required>
        <InputLabel id="create-schedule-cadence-label">Cadence</InputLabel>
        <Select
          labelId="create-schedule-cadence-label"
          label="Cadence"
          value={cadence}
          onChange={onCadenceChange}
        >
          {CADENCES.map((cadenceOption) => (
            <MenuItem key={cadenceOption} value={cadenceOption}>
              {cadenceOption}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth required>
        <InputLabel id="create-schedule-status-label">Status</InputLabel>
        <Select
          labelId="create-schedule-status-label"
          label="Status"
          value={status}
          onChange={onStatusChange}
        >
          {STATUSES.map((statusOption) => (
            <MenuItem key={statusOption} value={statusOption}>
              {statusOption}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label="Next due"
        type="datetime-local"
        value={nextDueLocal}
        onChange={onNextDueChange}
        required
        fullWidth
        InputLabelProps={DATETIME_INPUT_LABEL_PROPS}
      />
    </Stack>
  );
}

function CreateScheduleDialog({ open, onClose, editSchedule = null, onSaveSuccess }) {
  const isEdit = Boolean(editSchedule);
  const [crawlType, setCrawlType] = useState(defaultFormState().crawlType);
  const [targetType, setTargetType] = useState(defaultFormState().targetType);
  const [targetId, setTargetId] = useState(defaultFormState().targetId);
  const [cadence, setCadence] = useState(defaultFormState().cadence);
  const [status, setStatus] = useState(defaultFormState().status);
  const [nextDueLocal, setNextDueLocal] = useState(defaultFormState().nextDueLocal);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [severity, setSeverity] = useState('success');

  const resetForm = useCallback(() => {
    const formDefaults = defaultFormState();
    setCrawlType(formDefaults.crawlType);
    setTargetType(formDefaults.targetType);
    setTargetId(formDefaults.targetId);
    setCadence(formDefaults.cadence);
    setStatus(formDefaults.status);
    setNextDueLocal(formDefaults.nextDueLocal);
    setMessage(null);
    setSubmitting(false);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (editSchedule) {
      setCrawlType(editSchedule.crawlType ?? SCHEDULE_FIELD_DEFAULTS.crawlType);
      setTargetType(editSchedule.targetType ?? SCHEDULE_FIELD_DEFAULTS.targetType);
      setTargetId(editSchedule.targetId ?? '');
      setCadence(editSchedule.cadence ?? defaultFormState().cadence);
      setStatus(editSchedule.status ?? defaultFormState().status);
      setNextDueLocal(formatDateForDatetimeLocal(editSchedule.nextDueAt));
      setMessage(null);
      setSubmitting(false);
      return;
    }
    resetForm();
  }, [open, editSchedule, resetForm]);

  const showMsg = useCallback((text, sev = 'success') => {
    setSeverity(sev);
    setMessage(text);
  }, []);

  const handleClearMessage = useCallback(() => {
    setMessage(null);
  }, []);

  const handleCrawlTypeChange = useCallback((ev) => {
    setCrawlType(ev.target.value);
  }, []);

  const handleTargetTypeChange = useCallback((ev) => {
    setTargetType(ev.target.value);
  }, []);

  const handleTargetIdChange = useCallback((ev) => {
    setTargetId(ev.target.value);
  }, []);

  const handleCadenceChange = useCallback((ev) => {
    setCadence(ev.target.value);
  }, []);

  const handleStatusChange = useCallback((ev) => {
    setStatus(ev.target.value);
  }, []);

  const handleNextDueChange = useCallback((ev) => {
    setNextDueLocal(ev.target.value);
  }, []);

  const handleSubmit = useCallback(async () => {
    setMessage(null);
    const trimmedTargetId = targetId.trim();
    if (!trimmedTargetId) {
      showMsg('Target ID is required.', 'error');
      return;
    }
    if (!nextDueLocal) {
      showMsg('Next due date/time is required.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const nextDueDate = new Date(nextDueLocal);
      if (Number.isNaN(nextDueDate.getTime())) {
        showMsg('Invalid next due date.', 'error');
        setSubmitting(false);
        return;
      }

      if (editSchedule) {
        const breweryRef = doc(db, 'breweries', trimmedTargetId);
        const brewerySnap = await getDoc(breweryRef);
        if (!brewerySnap.exists()) {
          showMsg(`No brewery document found for id "${trimmedTargetId}".`, 'error');
          setSubmitting(false);
          return;
        }
        const scheduleRef = doc(db, CRAWL_SCHEDULES_COLLECTION, editSchedule.id);
        try {
          await updateDoc(scheduleRef, {
            cadence,
            status,
            nextDueAt: Timestamp.fromDate(nextDueDate),
            updatedAt: serverTimestamp(),
          });
        } catch (updateErr) {
          if (updateErr?.code === 'not-found') {
            showMsg(
              'This schedule no longer exists. It may have been deleted. Close the dialog and refresh the list.',
              'error',
            );
            setSubmitting(false);
            return;
          }
          throw updateErr;
        }
        onSaveSuccess?.();
        onClose();
        return;
      }

      const scheduleId = buildScheduleId(crawlType, targetType, trimmedTargetId);
      const breweryRef = doc(db, 'breweries', trimmedTargetId);
      const brewerySnap = await getDoc(breweryRef);
      if (!brewerySnap.exists()) {
        showMsg(`No brewery document found for id "${trimmedTargetId}".`, 'error');
        setSubmitting(false);
        return;
      }
      const scheduleRef = doc(db, CRAWL_SCHEDULES_COLLECTION, scheduleId);
      const scheduleSnap = await getDoc(scheduleRef);
      const existingRetryCount = scheduleSnap.exists()
        ? (scheduleSnap.data().retryCount ?? 0)
        : 0;
      await setDoc(
        scheduleRef,
        {
          crawlType,
          targetType,
          targetId: trimmedTargetId,
          cadence,
          status,
          retryCount: existingRetryCount,
          nextDueAt: Timestamp.fromDate(nextDueDate),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      onSaveSuccess?.();
      onClose();
    } catch (err) {
      console.error(err);
      showMsg(err?.message || 'Failed to save schedule.', 'error');
    } finally {
      setSubmitting(false);
    }
  }, [
    crawlType,
    targetType,
    targetId,
    cadence,
    status,
    nextDueLocal,
    showMsg,
    onClose,
    editSchedule,
    onSaveSuccess,
  ]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose();
  }, [onClose, submitting]);

  const messageAlert =
    message ? (
      <Alert severity={severity} sx={{ mb: 2 }} onClose={handleClearMessage}>
        {message}
      </Alert>
    ) : null;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? 'Edit schedule' : 'Create schedule'}</DialogTitle>
      <DialogContent>
        <CreateScheduleDialogIntro isEdit={isEdit} scheduleId={editSchedule?.id} />
        {messageAlert}
        <CreateScheduleFormFields
          crawlType={crawlType}
          targetType={targetType}
          targetId={targetId}
          cadence={cadence}
          status={status}
          nextDueLocal={nextDueLocal}
          onCrawlTypeChange={handleCrawlTypeChange}
          onTargetTypeChange={handleTargetTypeChange}
          onTargetIdChange={handleTargetIdChange}
          onCadenceChange={handleCadenceChange}
          onStatusChange={handleStatusChange}
          onNextDueChange={handleNextDueChange}
          identityFieldsDisabled={isEdit}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Save schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CreateScheduleDialog;
